// @tg-seq: 10797
// @architect-approved: HARDCODED_COLORS - Auto-whitelisted for white-label theme system (2026-06-16)

// [ROUTE] /TGsuperadmin/tgdesigncenter/mall-directory-test

import React, { useState, useMemo } from 'react';
import { getTsxFileName } from '../../utils/componentIdentity';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    Card,
    CardContent,
    Stack,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Grid,
    Divider,
    Avatar,
    Tooltip,
    Badge,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Search as SearchIcon,
    Business as BusinessIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Circle as CircleIcon,
    Info as InfoIcon,
    FilterList as FilterIcon,
    AccountCircle as AccountCircleIcon,
    CorporateFare as CorporateFareIcon,
} from '@mui/icons-material';

// 134-desk organizational hierarchy with sample user assignments
const organizationalData: Record<
    string,
    Record<
        string,
        Array<{
            tier: string;
            userName?: string;
            userTitle?: string;
            userEmail?: string;
            userPhone?: string;
            availabilityStatus?: 'online' | 'away' | 'busy' | 'offline';
            profilePhotoUrl?: string;
        }>
    >
> = {
    'Asset Management': {
        'Physical Plant': [
            {
                tier: 'Transactional',
                userName: 'Mike Torres',
                userTitle: 'Maintenance Technician',
                userEmail: 'm.torres@company.com',
                userPhone: '555-0101',
                availabilityStatus: 'online',
            },
            {
                tier: 'Supervision',
                userName: 'Sarah Chen',
                userTitle: 'Plant Supervisor',
                userEmail: 's.chen@company.com',
                userPhone: '555-0102',
                availabilityStatus: 'busy',
            },
        ],
        'Preventative Maintenance': [
            {
                tier: 'Transactional',
                userName: 'James Wilson',
                userTitle: 'PM Specialist',
                userEmail: 'j.wilson@company.com',
                availabilityStatus: 'online',
            },
            {
                tier: 'Supervision',
                userName: 'Lisa Rodriguez',
                userTitle: 'PM Supervisor',
                userEmail: 'l.rodriguez@company.com',
                availabilityStatus: 'away',
            },
        ],
        'ALL DeptGroups': [
            {
                tier: 'Process Mgt',
                userName: 'David Park',
                userTitle: 'Asset Process Manager',
                availabilityStatus: 'online',
            },
            {
                tier: 'Systems Config & Optimization',
                userName: 'Emily Thompson',
                userTitle: 'Asset Systems Lead',
                availabilityStatus: 'offline',
            },
            {
                tier: 'Strategic Mgt',
                userName: 'Robert Martinez',
                userTitle: 'VP Asset Management',
                availabilityStatus: 'busy',
            },
            { tier: 'Strategic Advisors' },
            {
                tier: 'Board - Ownership',
                userName: 'Jennifer Davis',
                userTitle: 'Board Member - Operations',
                availabilityStatus: 'offline',
            },
        ],
    },
    'Business Development': {
        'ALL DeptGroups': [
            {
                tier: 'Transactional',
                userName: 'Alex Johnson',
                userTitle: 'BizDev Specialist',
                userEmail: 'a.johnson@company.com',
                availabilityStatus: 'online',
            },
            {
                tier: 'Supervision',
                userName: 'Maria Garcia',
                userTitle: 'BizDev Manager',
                availabilityStatus: 'online',
            },
            {
                tier: 'Process Mgt',
                userName: 'Chris Anderson',
                userTitle: 'BizDev Process Lead',
                availabilityStatus: 'away',
            },
            { tier: 'Systems Config & Optimization' },
            {
                tier: 'Strategic Mgt',
                userName: 'Patricia Lee',
                userTitle: 'VP Business Development',
                availabilityStatus: 'busy',
            },
            { tier: 'Strategic Advisors' },
            { tier: 'Board - Ownership' },
        ],
    },
    Finance: {
        'Billing & A/R': [
            {
                tier: 'Transactional',
                userName: 'Jessica Brown',
                userTitle: 'A/R Specialist',
                userEmail: 'j.brown@company.com',
                availabilityStatus: 'online',
            },
            {
                tier: 'Supervision',
                userName: 'Michael Kim',
                userTitle: 'A/R Supervisor',
                userEmail: 'm.kim@company.com',
                availabilityStatus: 'online',
            },
        ],
        'Payroll & Analytics': [
            {
                tier: 'Transactional',
                userName: 'Daniel White',
                userTitle: 'Payroll Analyst',
                userEmail: 'd.white@company.com',
                availabilityStatus: 'away',
            },
            {
                tier: 'Supervision',
                userName: 'Amanda Taylor',
                userTitle: 'Payroll Manager',
                availabilityStatus: 'online',
            },
        ],
        'Travel & Expense': [
            {
                tier: 'Transactional',
                userName: 'Kevin Nguyen',
                userTitle: 'T&E Coordinator',
                availabilityStatus: 'online',
            },
            {
                tier: 'Supervision',
                userName: 'Rachel Green',
                userTitle: 'T&E Supervisor',
                availabilityStatus: 'offline',
            },
        ],
        'Audit & Compliance': [
            {
                tier: 'Transactional',
                userName: 'Brian Lewis',
                userTitle: 'Compliance Analyst',
                availabilityStatus: 'away',
            },
            {
                tier: 'Supervision',
                userName: 'Sophia Martinez',
                userTitle: 'Audit Manager',
                availabilityStatus: 'busy',
            },
        ],
        'ALL DeptGroups': [
            {
                tier: 'Process Mgt',
                userName: 'Thomas Anderson',
                userTitle: 'Finance Process Manager',
                availabilityStatus: 'online',
            },
            {
                tier: 'Systems Config & Optimization',
                userName: 'Laura Chen',
                userTitle: 'Finance Systems Lead',
                availabilityStatus: 'online',
            },
            {
                tier: 'Strategic Mgt',
                userName: 'Richard Thompson',
                userTitle: 'CFO',
                availabilityStatus: 'busy',
            },
            {
                tier: 'Strategic Advisors',
                userName: 'Margaret Wilson',
                userTitle: 'Finance Advisor',
                availabilityStatus: 'offline',
            },
            { tier: 'Board - Ownership' },
        ],
    },
    'Human Resources': {
        Recruiting: ['Transactional', 'Supervision'],
        'Workforce Planning': ['Transactional', 'Supervision'],
        'Performance Management': ['Transactional', 'Supervision'],
        'Talent Development': ['Transactional', 'Supervision'],
        'Employee Relations': ['Transactional', 'Supervision'],
        'Benefits Administration': ['Transactional', 'Supervision'],
        'ALL DeptGroups': [
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
    'Operations & Production': {
        Production: ['Transactional', 'Supervision'],
        'Quality Assurance': ['Transactional', 'Supervision'],
        Scheduling: ['Transactional', 'Supervision'],
        'Inventory & Materials': ['Transactional', 'Supervision'],
        Facilities: ['Transactional', 'Supervision'],
        'Environment Health & Safety': ['Transactional', 'Supervision'],
        'Process Improvement': ['Transactional', 'Supervision'],
        'ALL DeptGroups': [
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
    'Risk Mgt': {
        'ALL DeptGroups': [
            'Transactional',
            'Supervision',
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
    'Support Services': {
        'Reception & Concierge': ['Transactional', 'Supervision'],
        Administrative: ['Transactional', 'Supervision'],
        'Records & Documentation': ['Transactional', 'Supervision'],
        'Grounds & Janitorial': ['Transactional', 'Supervision'],
        Cafeteria: ['Transactional', 'Supervision'],
        'ALL DeptGroups': [
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
    'Supply Chain Mgt': {
        Procurement: ['Transactional', 'Supervision'],
        Logistics: ['Transactional', 'Supervision'],
        'Supplier Relations': ['Transactional', 'Supervision'],
        'Warehouse Operations': ['Transactional', 'Supervision'],
        'ALL DeptGroups': [
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
    'Technology & Information Systems': {
        'Software Development': ['Transactional', 'Supervision'],
        'System Administration': ['Transactional', 'Supervision'],
        'Network Operations': ['Transactional', 'Supervision'],
        'Data Management': ['Transactional', 'Supervision'],
        'User Support': ['Transactional', 'Supervision'],
        'Technology Procurement': ['Transactional', 'Supervision'],
        'ALL DeptGroups': [
            'Process Mgt',
            'Systems Config & Optimization',
            'Strategic Mgt',
            'Strategic Advisors',
            'Board - Ownership',
        ],
    },
};

// Tier color coding
// Helper function for tier colors
const getTierColor = (tier: string): string => {
    switch (tier) {
        case 'Transactional':
            return '#4caf50';
        case 'Supervision':
            return '#2196f3';
        case 'Process Mgt':
            return '#9c27b0';
        case 'Systems Config & Optimization':
            return '#ff9800';
        case 'Strategic Mgt':
            return '#f44336';
        case 'Strategic Advisors':
            return '#795548';
        case 'Board - Ownership':
            return '#607d8b';
        default:
            return '#9e9e9e';
    }
};

// Helper function for availability status
const getAvailabilityColor = (status: string): string => {
    switch (status) {
        case 'online':
            return '#4caf50';
        case 'away':
            return '#ff9800';
        case 'busy':
            return '#f44336';
        case 'offline':
            return '#9e9e9e';
        default:
            return '#9e9e9e';
    }
};

// Enhanced role card with user profile (like ArchitectureCommandCenter desk cards)
interface DeskUserCardProps {
    department: string;
    deptGroup: string;
    tier: string;
    userName?: string;
    userTitle?: string;
    userEmail?: string;
    userPhone?: string;
    availabilityStatus?: 'online' | 'away' | 'busy' | 'offline';
    profilePhotoUrl?: string;
    onClick?: () => void;
}

const DeskUserCard: React.FC<DeskUserCardProps> = ({
    department,
    deptGroup,
    tier,
    userName,
    userTitle,
    userEmail,
    userPhone,
    availabilityStatus = 'offline',
    profilePhotoUrl,
    onClick,
}) => {
    const tierColor = getTierColor(tier);
    const statusColor = getAvailabilityColor(availabilityStatus);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <Tooltip
            open={tooltipOpen}
            onOpen={() => setTooltipOpen(true)}
            onClose={() => setTooltipOpen(false)}
            title={
                userName ? (
                    <Box sx={{ p: 1 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            {userName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                            }}
                        >
                            {userTitle}
                        </Typography>
                        {userEmail && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                    alignItems: 'center',
                                    mt: 1,
                                }}
                            >
                                <EmailIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption">{userEmail}</Typography>
                            </Stack>
                        )}
                        {userPhone && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <PhoneIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption">{userPhone}</Typography>
                            </Stack>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            Status: {availabilityStatus.toUpperCase()}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="caption">No user assigned</Typography>
                )
            }
            placement="right"
            arrow
        >
            <Card
                variant="outlined"
                onClick={onClick}
                sx={{
                    borderLeft: `4px solid ${tierColor}`,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                    },
                }}
            >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={1.5}>
                        {/* Header: Role Name + Tier Badge */}
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                {deptGroup === 'ALL DeptGroups' ? 'Executive' : deptGroup}
                            </Typography>
                            <Chip
                                label={tier}
                                size="small"
                                sx={{
                                    bgcolor: tierColor,
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    height: 20,
                                }}
                            />
                        </Stack>

                        {/* User Profile Section */}
                        {userName ? (
                            <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        <CircleIcon
                                            sx={{
                                                fontSize: 12,
                                                color: statusColor,
                                                border: '2px solid white',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    }
                                >
                                    <Avatar
                                        src={profilePhotoUrl}
                                        sx={{ width: 36, height: 36, bgcolor: tierColor }}
                                    >
                                        {userName.charAt(0)}
                                    </Avatar>
                                </Badge>
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{
                                            fontWeight: 'medium',
                                        }}
                                    >
                                        {userName}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" noWrap>
                                        {userTitle || tier}
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.300' }}>
                                    <PersonIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Vacant Position
                                    </Typography>
                                </Box>
                            </Stack>
                        )}

                        {/* Department Tag */}
                        <Typography variant="caption" color="textSecondary" noWrap>
                            📍 {department}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        </Tooltip>
    );
};

const Self: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [infoOpen, setInfoOpen] = useState(false);
    const [filterMode, setFilterMode] = useState<'all' | 'myRoles' | 'byOrg'>('all');
    // Executive/C-Suite view toggle: OFF = ascending org hierarchy (Department > DeptGroup > Transactional/Supervision),
    // ON = Executive Suite cards only (Process Mgt/Systems Config/Strategic Mgt/Strategic Advisors/Board-Ownership).
    // Clicking an Executive Suite card drills back down into the ascending org hierarchy it oversees.
    const [showCSuite, setShowCSuite] = useState(false);

    // TODO: Replace with actual user context
    const currentUser = {
        name: 'Current User',
        userId: 'user-123',
        assignedRoles: [
            {
                department: 'Finance',
                deptGroup: 'Billing & A/R',
                tier: 'Supervision',
                orgName: 'Acme Corp',
            },
            {
                department: 'Finance',
                deptGroup: 'Audit & Compliance',
                tier: 'Transactional',
                orgName: 'TechCorp',
            },
        ],
    };

    const departments = Object.keys(organizationalData);
    const currentDepartment = departments[selectedTab];
    const deptGroups = organizationalData[currentDepartment];

    // Calculate statistics
    const totalDesks = useMemo(() => {
        let count = 0;
        Object.values(organizationalData).forEach((deptGroups) => {
            Object.values(deptGroups).forEach((tiers) => {
                count += tiers.length;
            });
        });
        return count;
    }, []);

    const currentDeptCount = useMemo(() => {
        let count = 0;
        Object.values(deptGroups).forEach((tiers) => {
            count += tiers.length;
        });
        return count;
    }, [deptGroups]);

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
            {/* Header */}
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                    borderRadius: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                }}
            >
                <Stack spacing={2}>
                    {/* Title Row */}
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                🏢 Organizational Mall Directory
                            </Typography>
                            <Typography variant="caption">
                                {totalDesks} Total Roles • {departments.length} Departments • 7
                                Management Tiers
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                color="inherit"
                                onClick={() => setInfoOpen(true)}
                                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                            >
                                <InfoIcon />
                            </IconButton>
                            <IconButton sx={{ color: 'white' }} onClick={() => setSearchOpen(true)}>
                                <SearchIcon />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {/* Filter Row */}
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <FilterIcon sx={{ fontSize: 20 }} />
                        <Chip
                            icon={<CorporateFareIcon />}
                            label="All Roles"
                            onClick={() => setFilterMode('all')}
                            sx={{
                                bgcolor:
                                    filterMode === 'all'
                                        ? 'rgba(255,255,255,0.3)'
                                        : 'rgba(255,255,255,0.1)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            }}
                        />
                        <Chip
                            icon={<AccountCircleIcon />}
                            label={`My Roles (${currentUser.assignedRoles.length})`}
                            onClick={() => setFilterMode('myRoles')}
                            sx={{
                                bgcolor:
                                    filterMode === 'myRoles'
                                        ? 'rgba(255,255,255,0.3)'
                                        : 'rgba(255,255,255,0.1)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            }}
                        />
                        <Chip
                            icon={<BusinessIcon />}
                            label="By Organization"
                            onClick={() => setFilterMode('byOrg')}
                            sx={{
                                bgcolor:
                                    filterMode === 'byOrg'
                                        ? 'rgba(255,255,255,0.3)'
                                        : 'rgba(255,255,255,0.1)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                            }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            {/* Department Tabs */}
            <Paper elevation={1} sx={{ borderRadius: 0 }}>
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue) => setSelectedTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 56,
                            textTransform: 'none',
                        },
                    }}
                >
                    {departments.map((dept) => {
                        const deskCount = Object.values(organizationalData[dept]).reduce(
                            (acc, tiers) => acc + tiers.length,
                            0,
                        );
                        return (
                            <Tab
                                key={dept}
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {dept}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {deskCount} roles
                                        </Typography>
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
            </Paper>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <Stack spacing={3}>
                    {/* Department Header */}
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <BusinessIcon sx={{ fontSize: 32 }} />
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {currentDepartment}
                                </Typography>
                                <Typography variant="caption">
                                    {currentDeptCount} roles across {Object.keys(deptGroups).length}{' '}
                                    functional areas
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showCSuite}
                                        onChange={(e) => setShowCSuite(e.target.checked)}
                                        color="default"
                                    />
                                }
                                label="👔 Executive / C-Suite View"
                                sx={{ color: 'white', whiteSpace: 'nowrap' }}
                            />
                        </Stack>
                    </Paper>

                    {/* Grouped by DeptGroup - ascending org hierarchy OR Executive Suite only, per showCSuite toggle */}
                    {Object.entries(deptGroups)
                        .filter(([deptGroup]) =>
                            showCSuite
                                ? deptGroup === 'ALL DeptGroups'
                                : deptGroup !== 'ALL DeptGroups',
                        )
                        .map(([deptGroup, tiers]) => (
                            <Box key={deptGroup}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Divider sx={{ flex: 1 }} />
                                    <Typography
                                        variant="subtitle1"
                                        color="primary"
                                        sx={{
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {deptGroup === 'ALL DeptGroups'
                                            ? '👔 Executive Suite'
                                            : `📂 ${deptGroup}`}
                                    </Typography>
                                    <Chip
                                        label={`${tiers.length} tiers`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Divider sx={{ flex: 1 }} />
                                </Stack>

                                <Grid container spacing={2}>
                                    {tiers.map((roleData, index) => {
                                        // Handle both old string format and new object format
                                        const tier =
                                            typeof roleData === 'string' ? roleData : roleData.tier;
                                        const userData =
                                            typeof roleData === 'object' ? roleData : {};

                                        return (
                                            <Grid
                                                key={`${deptGroup}-${tier}-${index}`}
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: 4,
                                                    lg: 3,
                                                }}
                                            >
                                                <DeskUserCard
                                                    department={currentDepartment}
                                                    deptGroup={deptGroup}
                                                    tier={tier}
                                                    {...userData}
                                                    onClick={
                                                        deptGroup === 'ALL DeptGroups'
                                                            ? () => setShowCSuite(false)
                                                            : undefined
                                                    }
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        ))}
                </Stack>
            </Box>

            {/* Tier Legend Footer */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 0 }}>
                <Stack
                    direction="row"
                    spacing={3}
                    sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 'bold',
                        }}
                    >
                        Management Tier Legend:
                    </Typography>
                    {[
                        { tier: 'Transactional', label: 'Tier 1' },
                        { tier: 'Supervision', label: 'Tier 2' },
                        { tier: 'Process Mgt', label: 'Tier 3' },
                        { tier: 'Systems Config & Optimization', label: 'Tier 4' },
                        { tier: 'Strategic Mgt', label: 'Tier 5' },
                        { tier: 'Strategic Advisors', label: 'Tier 6' },
                        { tier: 'Board - Ownership', label: 'Tier 7' },
                    ].map(({ tier, label }) => (
                        <Chip
                            key={tier}
                            label={`${label}: ${tier}`}
                            size="small"
                            sx={{
                                bgcolor: getTierColor(tier),
                                color: 'white',
                                fontSize: '0.75rem',
                            }}
                        />
                    ))}
                </Stack>
            </Paper>

            {/* Search Modal */}
            <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6">📍 Search Roles</Typography>
                        <IconButton onClick={() => setSearchOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by department, role, or tier..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                        sx={{ mt: 1 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                                ),
                            },
                        }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                            Try: "Finance", "Transactional", "HR Recruiting", "Executive"
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Info/Metadata Modal */}
            <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6">📊 Directory Information</Typography>
                        <IconButton onClick={() => setInfoOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* Current User Info */}
                        <Card variant="outlined">
                            <CardContent>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    👤 Current User
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Name:</strong> {currentUser.name}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Assigned Roles:</strong>{' '}
                                    {currentUser.assignedRoles.length}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                    variant="caption"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                        display: 'block',
                                    }}
                                >
                                    Active Role Assignments:
                                </Typography>
                                <Stack spacing={1}>
                                    {currentUser.assignedRoles.map((role, index) => (
                                        <Box key={index} sx={{ pl: 2 }}>
                                            <Typography variant="caption">
                                                • {role.orgName}: {role.department} →{' '}
                                                {role.deptGroup} ({role.tier})
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Organization Stats */}
                        <Card variant="outlined">
                            <CardContent>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    🏢 Organization Overview
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={6}>
                                        <Typography variant="body2">
                                            <strong>Total Departments:</strong> {departments.length}
                                        </Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <Typography variant="body2">
                                            <strong>Total Roles:</strong> {totalDesks}
                                        </Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <Typography variant="body2">
                                            <strong>Filled Positions:</strong> 30+
                                        </Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        <Typography variant="body2">
                                            <strong>Vacant Positions:</strong> 100+
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Future Features Note */}
                        <Card variant="outlined" sx={{ bgcolor: 'info.light' }}>
                            <CardContent>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                    }}
                                >
                                    🚀 Coming Soon (Tomorrow's Work)
                                </Typography>
                                <Typography variant="caption" component="div">
                                    • <strong>Company Logo Display:</strong> Show org logo in corner
                                    for multi-org users
                                    <br />• <strong>Role Count Badges:</strong> Display number badge
                                    for users with multiple roles
                                    <br />• <strong>Organization Pivot:</strong> Filter view by
                                    user's org associations
                                    <br />• <strong>User-Specific Version:</strong> Sliced component
                                    showing only user's assigned roles
                                    <br />• <strong>Export Directory:</strong> PDF/Excel export with
                                    custom filters
                                    <br />• <strong>Real-Time Sync:</strong> Live availability
                                    status from CollabCore
                                    <br />• <strong>Click Actions:</strong> Click-to-email,
                                    click-to-call, click-to-chat
                                    <br />• <strong>Mobile Responsive:</strong> Phone contact
                                    directory layout
                                </Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
